/** Changing an existing member's period — **gate 14**.
 *
 * This screen used to say, beside an Auditor with no engagement period, *"no route changes an
 * existing member's period yet, so until one does, suspend them rather than leave them as they
 * are"*. An Auditor invited for the wrong year, or a contractor whose contract was extended, had to
 * be suspended and invited again. The route exists now; this is the control.
 *
 * **What each role is offered is what the server will accept** (`claims.require_engagement`'s rules,
 * applied to a change): an Auditor has a window over the data *and* a last day of access, both
 * required, because an engagement that never ends is not one; every other role has an optional last
 * day and no window, because a window on a role that is not period-scoped restricts nothing and
 * reads like it does. A control a role cannot use is not rendered (design rule 6).
 *
 * **The only active Admin is not offered the control**, and the row says why. The server refuses an
 * end date for them (`last_admin`) for the reason it refuses a demotion: it reaches, later, a
 * workspace nobody can administer. Offering the field and refusing the value would be a control
 * that predictably fails.
 *
 * **Only what changed is sent.** The server separates "leave it" (key absent) from "no end date"
 * (explicit `null`), so a form that posted all three would turn an untouched end date into a
 * request to clear it - and, for an Auditor, into a refusal.
 *
 * **In force on the person's next request.** The window and the end are read from the membership on
 * every request, so nothing is revoked and nobody is signed out; the result says that, so a
 * narrowing is not mistaken for something that needs a restart.
 */

import { useState } from "react";

import { useChangeMemberPeriod } from "../api/orgCorrections";
import type { MemberPeriodChange } from "../api/orgCorrections";
import type { Member } from "../api/queries";
import own from "./People.module.css";
import styles from "./Screen.module.css";

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

function windowOf(raw: Member["period_scope"]): { from: string; to: string } | null {
  if (!raw || typeof raw !== "object") return null;
  const { from, to } = raw as { from?: unknown; to?: unknown };
  return typeof from === "string" && typeof to === "string" ? { from, to } : null;
}

export function EngagementPeriod({
  member,
  lastAdmin,
  onFailed,
}: {
  member: Member;
  /** The only active Admin. The server refuses them an end date; the row says so instead. */
  lastAdmin: boolean;
  /** A refusal goes to the screen's one place for them, beside the roster. */
  onFailed: (error: unknown) => void;
}) {
  const change = useChangeMemberPeriod();
  const [open, setOpen] = useState(false);
  const current = windowOf(member.period_scope);
  const auditor = member.role === "auditor";
  const [from, setFrom] = useState(current?.from ?? "");
  const [to, setTo] = useState(current?.to ?? "");
  const [end, setEnd] = useState(member.valid_to ?? "");

  if (member.status === "erased") return null;

  if (lastAdmin) {
    return (
      <span className={cls(own.why)} data-testid="period-last-admin">
        An end date for the only active Admin would leave nobody able to add one when it passes.
        Promote a colleague to Admin first.
      </span>
    );
  }

  if (!open) {
    // Wrapped so the link keeps its own width in the row's grid cell rather than stretching across it.
    return (
      <span className={cls(own.rowActions)}>
        <button
          type="button"
          className={styles.fixLink}
          data-testid={`change-period-${member.user_id}`}
          aria-label={`Change access period for ${member.display_name}`}
          onClick={() => {
            // Re-seeded from the member as they are now: a form abandoned half-typed must not come
            // back carrying dates the roster has since moved past.
            setFrom(current?.from ?? "");
            setTo(current?.to ?? "");
            setEnd(member.valid_to ?? "");
            setOpen(true);
          }}
        >
          Change access period
        </button>
      </span>
    );
  }

  // Only what differs. An Auditor's window travels as a pair, because the server validates the
  // resulting window as a whole and one end alone can be inverted by the other that is stored.
  const body: MemberPeriodChange = {};
  if (auditor && (from !== (current?.from ?? "") || to !== (current?.to ?? ""))) {
    body.scope_from = from;
    body.scope_to = to;
  }
  if (end !== (member.valid_to ?? "")) body.valid_to = end === "" ? null : end;
  const dirty = Object.keys(body).length > 0;
  const ready = dirty && (!auditor || (from !== "" && to !== "" && from <= to && end !== ""));

  return (
    <form
      className={cls(own.resendForm)}
      data-testid={`period-form-${member.user_id}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        change.mutate(
          { userId: member.user_id, ...body },
          { onSuccess: () => setOpen(false), onError: onFailed },
        );
      }}
    >
      {auditor ? (
        <>
          <label className={cls(own.field)}>
            Data from
            <input
              type="date"
              value={from}
              required
              data-testid="period-from"
              onChange={(event) => setFrom(event.target.value)}
            />
          </label>
          <label className={cls(own.field)}>
            Data to
            <input
              type="date"
              value={to}
              required
              data-testid="period-to"
              onChange={(event) => setTo(event.target.value)}
            />
          </label>
        </>
      ) : null}
      <label className={cls(own.field)}>
        Last day of access
        <input
          type="date"
          value={end}
          required={auditor}
          data-testid="period-end"
          onChange={(event) => setEnd(event.target.value)}
        />
        <span className={cls(own.hint)}>
          {auditor
            ? "An engagement is for a time, not for ever. It may be after the data window ends: an assurer reads a year during the next one."
            : "Leave it empty for no end date. To end somebody's access now, suspend them instead."}
        </span>
      </label>
      <span className={cls(own.hint)} data-testid="period-in-force-next-request">
        The change is in force on their next request. Nobody is signed out, and it is recorded in the
        audit log with what it replaced.
      </span>
      <span className={cls(own.rowActions)}>
        <button
          type="submit"
          className={styles.primary}
          disabled={!ready || change.isPending}
          data-testid="period-submit"
        >
          {change.isPending ? "Saving…" : "Save the period"}
        </button>
        <button
          type="button"
          className={styles.secondary}
          data-testid="period-cancel"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </span>
    </form>
  );
}
